<template>
  <div class="music-page">
    <header class="music-opening">
      <div>
        <p class="eyebrow">
          CHE-YU WU · {{ zh ? "鋼琴與聲音" : "PIANO & SOUND" }}
        </p>
        <h1>{{ $t("music.title") }}</h1>
        <blockquote>{{ $t("music.quote") }}</blockquote>
        <p class="counts">
          {{ data.allSongs.length }} {{ zh ? "首歌曲" : "tracks" }} ·
          {{ poemCount }} {{ zh ? "首詩" : "poems" }} ·
          {{ data.spotifyReleases.length }} {{ zh ? "筆發行" : "releases" }}
        </p>
        <div class="actions">
          <a
            class="primary"
            :href="spotify"
            target="_blank"
            rel="noopener noreferrer"
            >{{ zh ? "在 Spotify 收聽" : "Listen on Spotify" }} ↗︎</a
          ><a :href="streetvoice" target="_blank" rel="noopener noreferrer"
            >StreetVoice ↗︎</a
          >
        </div>
        <a class="text-link" href="#tracks"
          >{{ zh ? "瀏覽全部歌曲" : "Browse all tracks" }} ↓</a
        >
      </div>
      <figure>
        <SelectedPhoto id="huashan-performance" eager />
        <figcaption>
          <router-link :to="local('/exhibitions/algorithmic-poetry')">{{ zh ? '〈夢境與孤寂的邊界〉華山現場' : 'The Boundary of Dreams and Solitude at Huashan' }} ↗︎</router-link>
        </figcaption>
      </figure>
    </header>
    <nav class="music-nav" :aria-label="zh ? '音樂頁章節' : 'Music sections'">
      <a v-for="s in sections" :key="s.id" :href="'#' + s.id">{{ s.label }}</a>
    </nav>
    <section id="releases">
      <div class="section-head">
        <p class="eyebrow">01 · SPOTIFY</p>
        <h2>{{ zh ? "正式發行" : "Releases" }}</h2>
      </div>
      <div class="releases">
        <a
          v-for="r in data.spotifyReleases"
          :key="r.url"
          :href="r.url"
          target="_blank"
          rel="noopener noreferrer"
          ><span
            >{{ r.title }}<small lang="zh-Hant">{{ r.titleZh }}</small></span
          ><span>{{ r.year }} ↗︎</span></a
        >
      </div>
      <div class="actions">
        <button ref="playerTrigger" type="button" @click="togglePlayer">
          {{
            player
              ? zh
                ? "關閉播放器"
                : "Close player"
              : zh
                ? "載入 Spotify 播放器"
                : "Load Spotify player"
          }}</button
        ><a :href="spotify" target="_blank" rel="noopener noreferrer"
          >Spotify ↗︎</a
        >
      </div>
      <iframe
        v-if="player"
        class="spotify-player"
        title="Che-Yu Wu on Spotify"
        src="https://open.spotify.com/embed/artist/0AeZiCXhHzvexu47FOY5Tq?theme=0"
        height="352"
        allow="encrypted-media; fullscreen; picture-in-picture"
        @keydown.esc="togglePlayer"
      ></iframe>
    </section>
    <section id="sound">
      <div class="section-head">
        <p class="eyebrow">02 · LIVE & GENERATIVE</p>
        <h2>{{ zh ? "聲音走進現場" : "Sound in space" }}</h2>
      </div>
      <div class="sound-grid">
        <router-link
          v-for="w in soundWorks"
          :key="w.slug"
          :to="local('/work/' + w.slug)"
          ><img
            :src="w.cover"
            :alt="zh ? w.titleZh || w.title : w.title"
            loading="lazy"
          />
          <h3>{{ zh ? w.titleZh || w.title : w.title }} ↗︎</h3></router-link
        >
      </div>
    </section>
    <section id="tracks">
      <div class="section-head">
        <p class="eyebrow">03 · STREETVOICE · SONGS & POEMS</p>
        <h2>{{ zh ? "歌與詩" : "Songs & Poems" }}</h2>
        <p class="date-note">
          {{
            zh
              ? "每首歌都有自己的一頁：封面、日期、可以直接聽，還有發表時附的那段文字（長的是詩，短的是註記）。點封面或曲名進去，按「詩」在這裡展開。"
              : "Every track has its own page: the cover, the date, a player, and the lines posted with it (the longer ones are poems, the shorter ones notes). Open a cover or a title, or press “poem” to read it here."
          }}
        </p>
      </div>
      <ol class="cover-wall" :aria-label="zh ? '有自己封面的歌' : 'Tracks with their own cover'">
        <li v-for="t in ownCovers" :key="t.id">
          <router-link :to="local('/music/' + t.slug)" class="cover-tile">
            <img :src="t.cover.thumb" alt="" width="480" height="480" loading="lazy" decoding="async" />
            <span class="cover-tile-title">{{ trackName(t) }}</span>
            <span class="cover-tile-date">{{ (t.date || t.published).replace(/-/g, ".") }}</span>
          </router-link>
        </li>
      </ol>
      <p class="date-note cover-wall-note">{{ coverWallNote }}</p>
      <div class="filters">
        <label
          >{{ zh ? "搜尋曲名" : "Search titles"
          }}<input
            v-model="query"
            type="search"
            :placeholder="zh ? '輸入曲名' : 'Track title'" /></label
        ><label
          >{{ zh ? "年份" : "Year"
          }}<select v-model="year">
            <option value="all">{{ zh ? "所有年份" : "All years" }}</option>
            <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
            <option value="unknown">{{ zh ? "未註年份" : "Undated" }}</option>
          </select></label
        ><label class="filter-check"
          ><input v-model="poemsOnly" type="checkbox" />{{
            zh ? "只看有詩的" : "Poems only"
          }}</label
        ><button type="button" @click="clear">
          {{ zh ? "清除" : "Clear" }}
        </button>
      </div>
      <p class="date-note">
        {{
          zh
            ? "年份依原始曲名中的日期標示。"
            : "Years follow the dates in the original track titles."
        }}
      </p>
      <div class="result-row">
        <p class="result-count" aria-live="polite">
          {{ filtered.length }} / {{ data.allSongs.length }}
          {{ zh ? "首歌曲" : "tracks" }}
        </p>
        <button
          v-if="filteredPoems.length"
          type="button"
          class="text-link"
          @click="toggleAll"
        >
          {{
            allOpen
              ? zh
                ? "收合全部"
                : "Close all"
              : zh
                ? "展開全部詩"
                : "Open all poems"
          }}
        </button>
      </div>
      <ol class="tracks">
        <li v-for="song in filtered" :key="song.url">
          <div class="track-row">
            <span class="track-year">{{
              song.year || (zh ? "未註年份" : "Undated")
            }}</span>
            <router-link
              class="track-cover-link"
              :to="local('/music/' + song.page.slug)"
              tabindex="-1"
              aria-hidden="true"
              ><img
                class="track-cover"
                :src="song.page.cover?.thumb || song.cover"
                alt=""
                width="64"
                height="64"
                loading="lazy"
                decoding="async"
            /></router-link>
            <div class="track-main">
              <span class="track-title-row"
                ><router-link
                  class="track-title"
                  :to="local('/music/' + song.page.slug)"
                  >{{ song.title }}</router-link
                ><button
                  v-if="song.poem?.kind === 'poem'"
                  type="button"
                  class="track-toggle"
                  :aria-expanded="open.has(song.url)"
                  :aria-label="(zh ? '在這裡展開詩：' : 'Open the poem here: ') + song.title"
                  @click="toggle(song.url)"
                >
                  {{ zh ? "詩" : "poem" }}
                </button></span
              >
              <span
                v-if="song.poem?.kind === 'poem'"
                class="track-excerpt"
                :lang="!zh && song.poem.en ? 'en' : 'zh-Hant'"
                >{{ excerptOf(song) }}</span
              ><span
                v-else-if="song.poem"
                class="track-excerpt track-note"
                lang="zh-Hant"
                >{{ song.poem.zh.join("\n") }}</span
              >
            </div>
            <a
              class="track-platform"
              :href="song.url"
              target="_blank"
              rel="noopener noreferrer"
              >StreetVoice ↗︎</a
            >
          </div>
          <div
            v-if="song.poem?.kind === 'poem' && open.has(song.url)"
            class="poem"
            :class="{ single: !song.poem.en }"
          >
            <iframe
              class="sv-player"
              :src="embedOf(song)"
              :title="(zh ? 'StreetVoice 播放器：' : 'StreetVoice player: ') + song.title"
              width="330"
              height="100"
              loading="lazy"
              allow="autoplay; encrypted-media"
            ></iframe>
            <p lang="zh-Hant">{{ song.poem.zh.join("\n") }}</p>
            <p v-if="song.poem.en" lang="en">{{ song.poem.en.join("\n") }}</p>
            <router-link class="poem-page-link" :to="local('/music/' + song.page.slug)"
              >{{ zh ? "這首歌的頁面" : "This track's page" }} →</router-link
            >
          </div>
        </li>
      </ol>
      <p v-if="!filtered.length" class="empty">
        {{
          zh
            ? "找不到符合的歌曲。請調整搜尋或清除條件。"
            : "No matching tracks. Change your search or clear the filters."
        }}
      </p>
    </section>
    <section id="collections">
      <div class="section-head">
        <p class="eyebrow">04 · COLLECTIONS</p>
        <h2>
          {{ zh ? "合輯" : "Collections" }}
          <small>{{ data.streetVoiceAlbums.length }}</small>
        </h2>
      </div>
      <div class="collections">
        <a
          v-for="a in data.streetVoiceAlbums"
          :key="a.link"
          :href="a.link"
          target="_blank"
          rel="noopener noreferrer"
          ><img :src="a.cover" :alt="a.title" loading="lazy" />
          <h3>{{ a.title }} ↗︎</h3>
          <p>{{ a.subtitle }}</p></a
        >
      </div>
    </section>
    <section id="performances">
      <div class="section-head">
        <p class="eyebrow">05 · PERFORMANCE RECORDS</p>
        <h2>{{ zh ? "演出與錄音紀錄" : "Performances & recordings" }}</h2>
      </div>
      <article
        class="performance"
        v-for="(p, i) in data.performances"
        :key="p.title"
      >
        <span>{{ p.year }}</span>
        <div>
          <h3 :lang="zh && i < 2 ? 'zh-Hant' : 'en'">{{ performanceText(p).title }}</h3>
          <p v-if="!zh && i < 2" class="performance-original" lang="zh-Hant">{{ p.title }}</p>
          <p>{{ performanceText(p).venue }}</p>
          <p :lang="zh ? 'zh-Hant' : 'en'">{{ performanceText(p).desc }}</p>
          <a
            v-if="performanceLinks[i]?.startsWith('http')"
            :href="performanceLinks[i]"
            target="_blank"
            rel="noopener noreferrer"
            >{{ zh ? "查看紀錄" : "View record" }} ↗︎</a
          ><router-link
            v-else-if="performanceLinks[i]"
            :to="local(performanceLinks[i])"
            >{{ zh ? "查看作品" : "Explore work" }} ↗︎</router-link
          >
        </div>
      </article>
    </section>
    <section id="journey">
      <div class="section-head">
        <p class="eyebrow">06 · THE JOURNEY</p>
        <h2>{{ $t("music.journey.title") }}</h2>
      </div>
      <div class="journey">
        <article v-for="j in journey" :key="j.key">
          <span>{{ j.year }}</span>
          <h3>{{ $t("music.journey." + j.key) }}</h3>
          <p>{{ $t("music.journey." + j.key + "Desc") }}</p>
        </article>
      </div>
    </section>
    <footer class="music-footer">
      <a :href="spotify" target="_blank" rel="noopener noreferrer">Spotify ↗︎</a
      ><a :href="streetvoice" target="_blank" rel="noopener noreferrer"
        >StreetVoice ↗︎</a
      ><a
        href="https://www.youtube.com/channel/UCHX_XMhPrtvOmgGOnA2eTmA"
        target="_blank"
        rel="noopener noreferrer"
        >YouTube ↗︎</a
      >
    </footer>
  </div>
</template>
<script setup>
import { withSlash } from '@/runtime/site-path.mjs'
import SelectedPhoto from "@/components/photos/SelectedPhoto.vue";
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import data from "@/data/music-catalog.json";
import poems from "@/data/music-poems.json";
import musicTracks from "@/data/music-tracks.json";
import works from "@/data/work-catalog.json";
const { locale } = useI18n(),
  zh = computed(() => locale.value === "zh"),
  local = (p) => withSlash((zh.value ? "/zh" : "") + p);
const spotify = "https://open.spotify.com/artist/0AeZiCXhHzvexu47FOY5Tq",
  streetvoice = "https://streetvoice.com/frank890417/";
const player = ref(false),
  playerTrigger = ref(null),
  query = ref(""),
  year = ref("all"),
  poemsOnly = ref(false),
  open = ref(new Set());
const poemCount = Object.values(poems).filter((p) => p.kind === "poem").length;
function togglePlayer() {
  player.value = !player.value;
  playerTrigger.value?.focus();
}
function escape(e) {
  if (e.key === "Escape" && player.value) {
    player.value = false;
    playerTrigger.value?.focus();
  }
}
onMounted(() => window.addEventListener("keydown", escape));
onBeforeUnmount(() => window.removeEventListener("keydown", escape));
// 單曲頁（2026-10-04）：StreetVoice 網址 → music-tracks.json 那一筆（slug、封面縮圖）
const pageOf = new Map(musicTracks.tracks.map((t) => [t.url, t]));
// 顯示名帶日期碼（build-music-tracks.mjs 的 name；哲宇 10/04：「那是他的獨特日期編號」）
const trackName = (t) => (zh.value ? t.name?.zh || t.title.zh || t.title.en : t.name?.en || t.title.en || t.title.zh);
// 封面牆：有自己封面的歌（他後來多用自己拍的照片當封面）；共用同一張的照實寫在下面一行
const ownCovers = musicTracks.tracks.filter((t) => t.cover?.shared === 1);
const sharedCovers = [...new Map(musicTracks.tracks.filter((t) => t.cover?.shared > 1).map((t) => [t.cover.key, t.cover])).values()].sort((a, b) => b.shared - a.shared);
const coverWallNote = computed(() =>
  zh.value
    ? `${ownCovers.length} 首歌有自己的封面。` + sharedCovers.map((c, i) => `${c.shared} 首共用${i ? "另一張" : "同一張"}（${c.sharedYears}）`).join("，") + (sharedCovers.length ? "，點曲名一樣進得去。" : "")
    : `${ownCovers.length} tracks have a cover of their own. ` + sharedCovers.map((c, i) => `${c.shared} share ${i ? "another" : "one"} image (${c.sharedYears})`).join("; ") + (sharedCovers.length ? "; their titles below lead to their pages." : ""),
);
function titleYear(title) {
  const m = title.match(/^(\d{2})(\d{2})(\d{2})(?=\D|$)/);
  if (!m) return null;
  const y = 2000 + Number(m[1]),
    month = Number(m[2]),
    day = Number(m[3]),
    d = new Date(Date.UTC(y, month - 1, day));
  return d.getUTCFullYear() === y &&
    d.getUTCMonth() === month - 1 &&
    d.getUTCDate() === day
    ? String(y)
    : null;
}
const tracks = data.allSongs.map((s) => {
    const poem = poems[s.url] || null;
    return {
      ...s,
      year: titleYear(s.title),
      poem,
      page: pageOf.get(s.url),
    };
  }),
  embedOf = (song) =>
    "https://streetvoice.com/music/embed/?id=" +
    song.url.replace(/\/$/, "").split("/").pop() +
    "&s=s",
  firstLine = (lines) => (lines || []).find((line) => line.trim()) || "",
  excerptOf = (song) =>
    !zh.value && song.poem?.en
      ? firstLine(song.poem.en)
      : firstLine(song.poem?.zh),
  years = [...new Set(tracks.map((s) => s.year).filter(Boolean))]
    .sort()
    .reverse();
const filtered = computed(() =>
  tracks.filter(
    (s) =>
      (year.value === "all" || (s.year || "unknown") === year.value) &&
      (!poemsOnly.value || s.poem?.kind === "poem") &&
      s.title
        .toLocaleLowerCase()
        .includes(query.value.trim().toLocaleLowerCase()),
  ),
);
const filteredPoems = computed(() =>
    filtered.value.filter((s) => s.poem?.kind === "poem"),
  ),
  allOpen = computed(() =>
    filteredPoems.value.every((s) => open.value.has(s.url)),
  );
function toggle(url) {
  const next = new Set(open.value);
  next.has(url) ? next.delete(url) : next.add(url);
  open.value = next;
}
function toggleAll() {
  const next = new Set(open.value);
  for (const s of filteredPoems.value)
    allOpen.value ? next.delete(s.url) : next.add(s.url);
  open.value = next;
}
function clear() {
  query.value = "";
  year.value = "all";
  poemsOnly.value = false;
}
const soundWorks = [
  "the-last-input",
  "algorithmic-verses",
  "eternal-garden-in-a-bottle",
]
  .map((slug) => works.find((w) => w.slug === slug))
  .filter(Boolean);
function performanceText(performance) {
  return zh.value ? performance : data.performanceTranslations[performance.title].en;
}
const performanceLinks = [
  "/exhibitions/eternal-garden-in-a-bottle",
  "/exhibitions/algorithmic-poetry",
  "https://streetvoice.com/frank890417/playlists/917680/",
  null,
];
const journey = computed(() => [
  { key: "start", year: "2017" },
  { key: "burst", year: zh.value ? "2023 秋" : "Autumn 2023" },
  { key: "weaving", year: "2024" },
  { key: "algorithm", year: "2025" },
  { key: "spotify", year: "2025" },
  { key: "ircam", year: "2026" },
]);
const sections = computed(() => [
  { id: "releases", label: zh.value ? "發行" : "Releases" },
  { id: "sound", label: zh.value ? "聲音現場" : "Sound in space" },
  { id: "tracks", label: zh.value ? "歌曲" : "Tracks" },
  { id: "collections", label: zh.value ? "合輯" : "Collections" },
  { id: "performances", label: zh.value ? "演出" : "Performances" },
  { id: "journey", label: zh.value ? "歷程" : "Journey" },
]);
</script>
<style scoped>
.music-page {
  text-align: left;
  max-width: var(--editorial-w, 1440px);
  margin: auto;
  padding: 70px 40px 0;
  color: #eee9df;
  font-family: var(--font-body);
}
.music-opening {
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 64px;
  align-items: center;
  margin-bottom: 60px;
}
h1,
h2,
h3,
blockquote {
  font-family: var(--font-display);
  font-weight: 400;
}
h1 {
  font-size: clamp(64px, 8vw, 120px);
  line-height: 1;
  margin: 24px 0;
}
blockquote {
  font-size: clamp(22px, 2.5vw, 32px);
  margin: 24px 0;
  line-height: 1.6;
}
.eyebrow,
.counts,
.date-note,
.result-count {
  color: #b7ad9b;
  font-size: 13px;
  letter-spacing: 0.08em;
}
.music-opening figure {
  margin: 0;
}
.music-opening img {
  width: 100%;
  height: auto;
  display: block;
}
.music-opening figcaption {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-top: 16px;
}
.music-opening small {
  color: #b7ad9b;
  font-size: 11px;
}
a {
  color: #c5af7f;
  text-decoration: none;
}
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 24px 0;
}
.actions a,
.actions button,
.filters button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 10px 22px;
  border: 0;
  border-radius: var(--r-pill);
  color: var(--ink);
  background: var(--tint-2);
  font: inherit;
  cursor: pointer;
  transition: var(--t-hover);
}
.actions a:hover,
.actions button:hover,
.filters button:hover {
  background: var(--tint-3);
}
.actions .primary,
.actions .primary:hover {
  background: #c5af7f;
  color: #080808;
}
.text-link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
}
a:focus-visible,
button:focus-visible,
input:focus-visible,
select:focus-visible {
  outline: 2px solid #d4be8b;
  outline-offset: 4px;
}
.music-nav {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  border-top: 1px solid #514a3e;
  border-bottom: 1px solid #514a3e;
  padding: 12px 0;
}
.music-nav a {
  display: flex;
  min-height: 44px;
  align-items: center;
}
section {
  padding: 70px 0;
  border-bottom: 1px solid #37332b;
  scroll-margin-top: 90px;
}
.section-head {
  margin-bottom: 32px;
}
h2 {
  font-size: clamp(32px, 4vw, 48px);
  margin: 10px 0;
}
h2 small {
  font-size: 16px;
  color: #b7ad9b;
}
h3 {
  font-size: 22px;
  line-height: 1.45;
  margin: 12px 0;
}
.releases {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 48px;
}
.releases a {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  min-height: 92px;
  padding: 20px 0;
  border-bottom: 1px solid #37332b;
  font-size: 22px;
  color: #eee9df;
}
.releases small {
  display: block;
  color: #b7ad9b;
  font-size: 15px;
  margin-top: 6px;
}
.releases a > span:last-child {
  font-size: 14px;
  white-space: nowrap;
  color: #c5af7f;
}
.spotify-player {
  width: 100%;
  border: 0;
  border-radius: 12px;
}
.sound-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}
.sound-grid img {
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: cover;
}
.sound-grid h3 {
  font-size: 23px;
}
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  align-items: end;
}
.filters label {
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: #b7ad9b;
  font-size: 14px;
}
.filters label:first-child {
  flex: 1;
  min-width: 200px;
}
input,
select {
  background: var(--bg2);
  color: #eee9df;
  border: 0;
  border-radius: var(--r-md);
  padding: 12px 16px;
  font: inherit;
  min-height: 46px;
  max-width: 100%;
}
.tracks {
  padding: 0;
  list-style: none;
  margin: 26px 0 0;
}
.filters .filter-check {
  flex-direction: row;
  align-items: center;
  min-height: 46px;
  cursor: pointer;
}
.filters .filter-check input {
  min-height: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  margin: 0;
  accent-color: #c5af7f;
}
.result-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px 24px;
}
.result-row button.text-link {
  border: 0;
  padding: 0;
  background: transparent;
  color: #c5af7f;
  font: inherit;
  font-size: 13px;
  letter-spacing: 0.08em;
  cursor: pointer;
}
.tracks li {
  border-bottom: 1px solid #28251f;
}
.track-row {
  display: grid;
  grid-template-columns: 90px 64px 1fr auto;
  align-items: center;
  gap: 20px;
  min-height: 72px;
  padding: 18px 0;
  color: #eee9df;
}
.track-year,
.track-platform {
  color: #b7ad9b;
  font-size: 13px;
}
.track-platform {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: #c5af7f;
  white-space: nowrap;
}
.track-cover-link {
  display: block;
  line-height: 0;
}
.track-cover {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 3px;
  background: #1a1813;
  display: block;
}
.track-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.track-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 14px;
}
.track-title {
  font-size: 18px;
  overflow-wrap: anywhere;
  color: #eee9df;
}
a.track-title:hover {
  color: #c5af7f;
}
.track-toggle {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 2px 12px;
  border: 1px solid #4a4438;
  border-radius: var(--r-pill);
  background: transparent;
  color: #b7ad9b;
  font: inherit;
  font-size: 12px;
  letter-spacing: 0.06em;
  cursor: pointer;
}
.track-toggle:hover {
  border-color: #c5af7f;
  color: #c5af7f;
}
.track-toggle::after {
  content: " +";
}
.track-toggle[aria-expanded="true"]::after {
  content: " −";
}
.poem-page-link {
  grid-column: 1 / -1;
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  font-size: 14px;
}
/* 封面牆（2026-10-04）：有自己封面的歌，點進單曲頁 */
.cover-wall {
  list-style: none;
  margin: 8px 0 14px;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 22px 18px;
}
.cover-tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #eee9df;
}
.cover-tile img {
  width: 100%;
  height: auto;
  aspect-ratio: 1;
  object-fit: cover;
  display: block;
  margin-bottom: 8px;
  background: #1a1813;
  transition: opacity 0.2s;
}
.cover-tile:hover img {
  opacity: 0.86;
}
.cover-tile-title {
  font-size: 15px;
  line-height: 1.4;
}
.cover-tile:hover .cover-tile-title {
  color: #c5af7f;
}
.cover-tile-date {
  font-size: 12px;
  color: #b7ad9b;
  letter-spacing: 0.04em;
}
.cover-wall-note {
  margin-bottom: 40px;
}
.track-excerpt {
  color: #b7ad9b;
  font-size: 13px;
  line-height: 1.6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.track-note {
  white-space: pre-line;
  overflow: visible;
}
.poem {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  padding: 0 0 28px 178px;
}
.poem.single {
  grid-template-columns: 1fr;
  max-width: 34em;
}
.sv-player {
  grid-column: 1 / -1;
  width: 330px;
  max-width: 100%;
  height: 100px;
  border: 0;
  border-radius: var(--r-md);
  background: #fff;
  margin: 4px 0 28px;
}
.poem p {
  margin: 0;
  font-family: var(--font-display);
  font-size: 17px;
  line-height: 2;
  color: #e4dfd3;
  white-space: pre-line;
}
.empty {
  padding: 40px 0;
}
.collections {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 24px;
}
.collections img {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
}
.collections h3 {
  font-size: 20px;
}
.collections p {
  font-size: 13px;
  color: #b7ad9b;
}
.performance {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 24px;
  padding: 24px 0;
  border-bottom: 1px solid #28251f;
}
.performance > span {
  color: #b7ad9b;
}
.performance h3 {
  margin-top: 0;
}
.performance p {
  color: #c2bcaf;
  line-height: 1.8;
}
.performance a {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
}
.journey {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}
.journey article {
  border-top: 1px solid #81745d;
  padding-top: 22px;
}
.journey span {
  color: #c5af7f;
  font-size: 14px;
}
.journey p {
  line-height: 1.85;
  color: #c2bcaf;
}
.music-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: 44px 0;
}
.music-footer a {
  min-height: 44px;
  display: flex;
  align-items: center;
}
@media (max-width: 1000px) {
  .music-opening {
    gap: 32px;
  }
  .collections {
    grid-template-columns: repeat(3, 1fr);
  }
  .sound-grid {
    gap: 18px;
  }
  .journey {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 640px) {
  .music-page {
    padding: 36px 22px 0;
  }
  .music-opening {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .music-opening figure {
    margin-top: 8px;
  }
  .music-nav {
    gap: 8px 20px;
  }
  .releases,
  .sound-grid,
  .journey {
    grid-template-columns: 1fr;
  }
  .collections {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px 18px;
  }
  section {
    padding: 48px 0;
  }
  .track-row {
    grid-template-columns: 56px 48px 1fr;
    gap: 10px;
  }
  .track-cover {
    width: 48px;
    height: 48px;
  }
  /* 手機：封面牆改成橫向滑動的一排，不把曲目清單推到三千像素以下 */
  .cover-wall {
    display: flex;
    gap: 14px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    margin-right: -22px;
    padding-right: 22px;
    padding-bottom: 8px;
  }
  .cover-wall li {
    flex: 0 0 42vw;
    scroll-snap-align: start;
  }
  .track-platform {
    grid-column: 3;
  }
  .track-title {
    font-size: 17px;
  }
  .poem {
    grid-template-columns: 1fr;
    gap: 24px;
    padding-left: 0;
  }
  .performance {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  .releases a {
    font-size: 20px;
  }
  .filters label:first-child {
    width: 100%;
  }
  blockquote {
    margin: 20px 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  * {
    scroll-behavior: auto !important;
    transition: none !important;
  }
}
</style>
